import {
  AccountUserRolesListResponse,
  DeleteAccountUserRoleRequest,
  DeleteAccountUserRoleResponse,
  SubAccountsRequest,
  SubAccountsResponse,
} from 'vopay';

export type PropagateRoleDeleteResult = {
  numTotal: number;
  numDeleted: number;
  skipped: string[];
  failed: string[];
};

type Deps = {
  fetchSubAccountsAsync: (params: SubAccountsRequest) => Promise<SubAccountsResponse>;
  fetchAccountUserRolesWithBearer: (bearer: string) => Promise<AccountUserRolesListResponse>;
  deleteUserRole: (params: DeleteAccountUserRoleRequest, bearer?: string) => Promise<DeleteAccountUserRoleResponse>;
};

/**
 * Fan-out helper for deleting a role across sub-accounts. Each sub-account
 * has its own RoleID for the same logical role, so we have to look it up by
 * Name first (using the sub-account's bearer) then call delete with that
 * sub-account's RoleID + bearer.
 *
 * Sub-accounts with no matching role by name are reported as `skipped` —
 * nothing to delete there.
 *
 * A failure of the outer sub-accounts fetch throws so the caller doesn't
 * mistake it for "zero sub-accounts to clean up".
 */
export async function propagateRoleDeleteToSubAccounts(
  roleName: string,
  { fetchSubAccountsAsync, fetchAccountUserRolesWithBearer, deleteUserRole }: Deps,
): Promise<PropagateRoleDeleteResult> {
  const subAccountsRes = await fetchSubAccountsAsync({ ShowToken: true, ActiveOnly: true });

  if (!subAccountsRes.Success) {
    throw new Error(subAccountsRes.ErrorMessage || 'Failed to fetch sub-accounts');
  }

  const subAccounts = subAccountsRes.Subaccounts;

  const results = await Promise.all(
    subAccounts.map(async (subAccount) => {
      const label = subAccount.AccountName || subAccount.AccountID;

      try {
        const rolesRes = await fetchAccountUserRolesWithBearer(subAccount.Token);

        if (!rolesRes.Success) {
          return { kind: 'failed', label };
        }

        const matchedRole = rolesRes.Roles.find((role) => role.Name === roleName);

        if (!matchedRole) {
          return { kind: 'skipped', label };
        }

        await deleteUserRole({ RoleID: matchedRole.RoleID }, subAccount.Token);

        return { kind: 'deleted', label };
      } catch (error) {
        console.error(`Failed to delete role "${roleName}" in sub-account ${label}:`, error);

        return { kind: 'failed', label };
      }
    }),
  );

  return {
    numTotal: subAccounts.length,
    numDeleted: results.filter((r) => r.kind === 'deleted').length,
    skipped: results.filter((r) => r.kind === 'skipped').map((r) => r.label),
    failed: results.filter((r) => r.kind === 'failed').map((r) => r.label),
  };
}
